import { getListingAvailability, getParisDateKey } from '../../util/listingAvailability.shared';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const formatPriceLabel = (price, locale = 'fr-FR') => {
  if (!price || !Number.isFinite(price.amount)) return null;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: price.currency || 'EUR',
      maximumFractionDigits: 0,
    }).format(price.amount / 100);
  } catch (e) {
    return `${Math.round(price.amount / 100)} €`;
  }
};

const escapeHtml = s =>
  String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const STYLE_TAG_ID = 'cobuco-search-map-styles';
const ensureStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_TAG_ID;
  style.textContent = `
    .nam-marker { cursor: pointer; user-select: none; will-change: transform; z-index: 1; }
    .nam-marker.is-active { z-index: 5; }
    .nam-marker:hover { z-index: 4; }
    .nam-marker-inner {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 5px 11px;
      background: linear-gradient(135deg, #6d3ee0 0%, #5c35c7 100%);
      color: #fff;
      font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
      font-size: 11.5px;
      font-weight: 800;
      letter-spacing: 0.2px;
      border-radius: 20px;
      white-space: nowrap;
      border: 2px solid #fff;
      box-shadow:
        0 3px 10px rgba(92, 53, 199, 0.4),
        0 1px 3px rgba(0, 0, 0, 0.08);
      transition: transform 180ms cubic-bezier(.2,.7,.3,1), box-shadow 180ms ease, background 180ms ease;
      transform-origin: center bottom;
    }
    .nam-marker-inner::after {
      content: '';
      position: absolute;
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
      width: 10px;
      height: 10px;
      background: inherit;
      border-right: 2px solid #fff;
      border-bottom: 2px solid #fff;
      border-radius: 2px;
      box-shadow: 0 3px 4px rgba(92, 53, 199, 0.18);
      z-index: -1;
    }
    .nam-marker:hover .nam-marker-inner,
    .nam-marker.is-active .nam-marker-inner {
      background: linear-gradient(135deg, #4f2bb0 0%, #3f1e84 100%);
      transform: scale(1.18);
      box-shadow: 0 14px 28px rgba(63, 30, 132, 0.5), 0 3px 6px rgba(0, 0, 0, 0.12);
    }
    .nam-marker-count {
      position: absolute;
      top: -10px;
      right: -12px;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #fff;
      color: #5c35c7;
      border: 2px solid #5c35c7;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 800;
      line-height: 1;
      box-shadow: 0 3px 8px rgba(31, 10, 84, 0.22);
    }
    .nam-popup {
      z-index: 100;
    }
    .nam-popup .mapboxgl-popup-content {
      padding: 0;
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 18px 50px rgba(31, 10, 84, 0.18), 0 4px 12px rgba(0, 0, 0, 0.06);
      border: 1px solid rgba(92, 53, 199, 0.08);
      font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    }
    .nam-popup .mapboxgl-popup-tip {
      border-top-color: #fff;
      border-bottom-color: #fff;
    }
    /* Uniform-size popup so every marker reveals the same compact card */
    .nam-popup .mapboxgl-popup-content {
      width: 260px;
    }
    .nam-popup-card {
      width: 260px;
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .nam-popup-img {
      width: 100%;
      height: 110px;
      object-fit: cover;
      display: block;
      background: linear-gradient(135deg, #efeaff 0%, #d9d0fa 100%);
      flex-shrink: 0;
    }
    .nam-popup-body {
      padding: 10px 12px 8px;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .nam-popup-eyebrow {
      display: inline-block;
      align-self: flex-start;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      color: #5c35c7;
      background: rgba(92, 53, 199, 0.1);
      padding: 2px 7px;
      border-radius: 999px;
      line-height: 1.4;
    }
    .nam-popup-title {
      font-size: 13px;
      font-weight: 800;
      color: #1f0a54;
      margin: 0;
      line-height: 1.25;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      min-height: 32px;
    }
    .nam-popup-address {
      font-size: 11px;
      color: #6b7280;
      margin: 0;
      line-height: 1.3;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .nam-popup-meta {
      display: flex;
      gap: 8px;
      font-size: 11px;
      color: #1f0a54;
      font-weight: 600;
      margin: 0;
    }
    .nam-popup-meta-dot {
      color: rgba(31, 10, 84, 0.25);
    }
    .nam-popup-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-top: 2px;
    }
    .nam-popup-price {
      font-size: 14px;
      font-weight: 800;
      color: #5c35c7;
      margin: 0;
      white-space: nowrap;
    }
    .nam-popup-price-unit {
      font-size: 10px;
      font-weight: 600;
      color: #9ca3af;
      margin-left: 2px;
    }
    .nam-popup-author {
      font-size: 10.5px;
      color: #6b7280;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .nam-popup-author-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #5c35c7;
      flex-shrink: 0;
    }
    .nam-popup-cta {
      display: block;
      width: 100%;
      border: 0;
      cursor: pointer;
      font-family: inherit;
      text-align: center;
      background: linear-gradient(135deg, #6d3ee0 0%, #5c35c7 100%);
      color: #fff !important;
      padding: 9px 14px;
      font-size: 11.5px;
      font-weight: 700;
      letter-spacing: 0.3px;
      text-decoration: none !important;
      margin: 0;
      transition: background 160ms ease;
    }
    .nam-popup-cta:hover {
      background: linear-gradient(135deg, #4f2bb0 0%, #3f1e84 100%);
    }
    .nam-popup-cta:focus-visible {
      outline: 3px solid #cbb8ff;
      outline-offset: -3px;
    }
    /* Group popup (multiple listings at the same espace) */
    .nam-popup-group-img {
      position: relative;
      width: 100%;
      height: 110px;
      overflow: hidden;
      flex-shrink: 0;
      background: linear-gradient(135deg, #efeaff 0%, #d9d0fa 100%);
    }
    .nam-popup-group-img img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .nam-popup-group-img::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, rgba(31, 10, 84, 0) 35%, rgba(31, 10, 84, 0.78) 100%);
      pointer-events: none;
    }
    .nam-popup-group-imgChip {
      position: absolute;
      top: 10px;
      left: 10px;
      z-index: 1;
      background: rgba(255, 255, 255, 0.95);
      color: #5c35c7;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      padding: 4px 9px;
      border-radius: 999px;
      box-shadow: 0 2px 8px rgba(31, 10, 84, 0.18);
    }
    .nam-popup-group-imgOverlay {
      position: absolute;
      left: 12px;
      right: 12px;
      bottom: 10px;
      z-index: 1;
      color: #fff;
    }
    .nam-popup-group-overlayTitle {
      font-size: 14px;
      font-weight: 800;
      line-height: 1.2;
      margin: 0 0 2px;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .nam-popup-group-overlaySub {
      font-size: 10.5px;
      font-weight: 500;
      opacity: 0.92;
      margin: 0;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .nam-popup-group-summary {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 10px 14px;
      border-bottom: 1px solid rgba(92, 53, 199, 0.08);
      background: #fafaff;
    }
    .nam-popup-group-summaryLeft {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      color: #6b7280;
      font-weight: 600;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .nam-popup-group-summaryDot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #5c35c7;
      flex-shrink: 0;
    }
    .nam-popup-group-summaryRight {
      font-size: 11px;
      color: #5c35c7;
      font-weight: 800;
      flex-shrink: 0;
    }
    .nam-popup-group-summaryRight small {
      color: #9ca3af;
      font-weight: 600;
      margin-right: 3px;
    }
    .nam-popup-list {
      list-style: none;
      margin: 0;
      padding: 0;
      max-height: 200px;
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: rgba(92, 53, 199, 0.4) transparent;
    }
    .nam-popup-list::-webkit-scrollbar { width: 6px; }
    .nam-popup-list::-webkit-scrollbar-thumb {
      background: rgba(92, 53, 199, 0.3);
      border-radius: 3px;
    }
    .nam-popup-list li {
      border-bottom: 1px solid rgba(92, 53, 199, 0.06);
    }
    .nam-popup-list li:last-child { border-bottom: none; }
    .nam-popup-list a {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 9px 14px;
      text-decoration: none;
      color: inherit;
      transition: background 120ms ease;
    }
    .nam-popup-list a:hover { background: rgba(92, 53, 199, 0.06); }
    .nam-popup-list-main {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
      flex: 1;
    }
    .nam-popup-list-title {
      font-size: 12px;
      font-weight: 700;
      color: #1f0a54;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .nam-popup-list-meta {
      font-size: 10px;
      color: #6b7280;
      font-weight: 500;
    }
    .nam-popup-list-price {
      font-size: 12.5px;
      font-weight: 800;
      color: #5c35c7;
      flex-shrink: 0;
      white-space: nowrap;
      letter-spacing: -0.2px;
    }
  `;
  document.head.appendChild(style);
};

const buildSinglePopupHtml = ({
  title,
  availabilityLabel,
  streetCity,
  formattedPrice,
  unit,
  offerType,
  slug,
  id,
  imageUrl,
  sqm,
  cap,
  authorName,
}) => {
  const metaParts = [];
  if (sqm) metaParts.push(`<span>${escapeHtml(sqm)}</span>`);
  if (cap) metaParts.push(`<span>${escapeHtml(cap)}</span>`);
  const metaHtml = metaParts.length
    ? `<div class="nam-popup-meta">${metaParts.join(
        '<span class="nam-popup-meta-dot">·</span>'
      )}</div>`
    : '';

  const priceAuthorRow =
    formattedPrice || authorName
      ? `<div class="nam-popup-row">
        ${
          formattedPrice
            ? `<span class="nam-popup-price">${escapeHtml(
                formattedPrice
              )}<span class="nam-popup-price-unit">${escapeHtml(unit)}</span></span>`
            : '<span></span>'
        }
        ${
          authorName
            ? `<span class="nam-popup-author"><span class="nam-popup-author-dot"></span>${escapeHtml(
                authorName
              )}</span>`
            : ''
        }
      </div>`
      : '';

  return `
    <div class="nam-popup-card">
      ${
        imageUrl
          ? `<img class="nam-popup-img" src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title) ||
              ''}" />`
          : '<div class="nam-popup-img"></div>'
      }
      <div class="nam-popup-body">
        <span class="nam-popup-eyebrow">${offerType === 'sale' ? 'À vendre' : 'À louer'}</span>
        <h4 class="nam-popup-title">${escapeHtml(title) || 'Annonce'}</h4>
        ${streetCity ? `<p class="nam-popup-address">${escapeHtml(streetCity)}</p>` : ''}
        ${
          availabilityLabel
            ? `<p class="nam-popup-address">${escapeHtml(availabilityLabel)}</p>`
            : ''
        }
        ${metaHtml}
        ${priceAuthorRow}
      </div>
      <a class="nam-popup-cta" href="/l/${escapeHtml(slug)}/${escapeHtml(id)}">Voir l'annonce →</a>
    </div>
  `;
};

const buildGroupPopupHtml = ({
  groupTitle,
  streetCity,
  items,
  imageUrl,
  authorName,
  minFormattedPrice,
}) => {
  const rows = items
    .map(item => {
      const metaParts = [];
      if (item.sqm) metaParts.push(escapeHtml(item.sqm));
      if (item.cap) metaParts.push(escapeHtml(item.cap));
      const meta = metaParts.length
        ? `<span class="nam-popup-list-meta">${metaParts.join(' · ')}</span>`
        : '';
      return `
        <li>
          <a href="/l/${escapeHtml(item.slug)}/${escapeHtml(item.id)}">
            <span class="nam-popup-list-main">
              <span class="nam-popup-list-title">${escapeHtml(item.bureauLabel)}</span>
              ${meta}
              ${
                item.availabilityLabel
                  ? `<span class="nam-popup-list-meta">${escapeHtml(item.availabilityLabel)}</span>`
                  : ''
              }
            </span>
            <span class="nam-popup-list-price">${escapeHtml(item.formattedPrice)}</span>
          </a>
        </li>
      `;
    })
    .join('');

  const imgInner = imageUrl
    ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(groupTitle) || ''}" />`
    : '';

  return `
    <div class="nam-popup-card">
      <div class="nam-popup-group-img">
        ${imgInner}
        <span class="nam-popup-group-imgChip">${items.length} annonces</span>
        <div class="nam-popup-group-imgOverlay">
          <p class="nam-popup-group-overlayTitle">${escapeHtml(groupTitle) || 'Espace'}</p>
          ${streetCity ? `<p class="nam-popup-group-overlaySub">${escapeHtml(streetCity)}</p>` : ''}
        </div>
      </div>
      <div class="nam-popup-group-summary">
        <span class="nam-popup-group-summaryLeft">
          ${
            authorName
              ? `<span class="nam-popup-group-summaryDot"></span>${escapeHtml(authorName)}`
              : 'Espaces'
          }
        </span>
        ${
          minFormattedPrice
            ? `<span class="nam-popup-group-summaryRight"><small>dès</small>${escapeHtml(
                minFormattedPrice
              )}</span>`
            : ''
        }
      </div>
      <ul class="nam-popup-list">${rows}</ul>
    </div>
  `;
};

const buildLocationPopupHtml = listing => {
  const count = listing.listingCount;
  const available = listing.availableCount || 0;
  const price = available > 0 ? formatPriceLabel(listing.price) : null;
  const availabilityLabel = available
    ? `${available} bureau${available > 1 ? 'x' : ''} disponible${available > 1 ? 's' : ''}`
    : 'Actuellement indisponible';
  return `
    <div class="nam-popup-card">
      ${
        listing.imageUrl
          ? `<img class="nam-popup-img" src="${escapeHtml(listing.imageUrl)}" alt="${escapeHtml(
              listing.title
            )}" />`
          : '<div class="nam-popup-img"></div>'
      }
      <div class="nam-popup-body">
        <span class="nam-popup-eyebrow">${escapeHtml(count)} bureau${
    count > 1 ? 'x' : ''
  } dans ce lieu</span>
        <h4 class="nam-popup-title">${escapeHtml(listing.title) || 'Lieu'}</h4>
        ${
          listing.streetCity
            ? `<p class="nam-popup-address">${escapeHtml(listing.streetCity)}</p>`
            : ''
        }
        <p class="nam-popup-address">${availabilityLabel}</p>
        ${
          price
            ? `<p class="nam-popup-price">Dès ${escapeHtml(
                price
              )}<span class="nam-popup-price-unit">${escapeHtml(listing.unit)}</span></p>`
            : ''
        }
      </div>
      <button type="button" class="nam-popup-cta" data-location-id="${escapeHtml(listing.id)}">${
    count === 1 ? 'Voir le bureau' : `Voir les ${escapeHtml(count)} bureaux`
  } →</button>
    </div>
  `;
};

const buildBureauLabel = listing => {
  const title = listing?.title || '';
  // Cobuco listing titles look like 'Bureau privatif 1.1 — Work & Share ...'.
  // Keep the part before ' — ' so the list row stays tight.
  const head = title.split(' — ')[0];
  return head || 'Bureau';
};

const groupByCoords = listings => {
  const groups = new Map();
  listings.forEach(listing => {
    if (!listing?.geo || !Number.isFinite(listing.geo.lat) || !Number.isFinite(listing.geo.lng))
      return;
    // A location already groups offices from one advertiser and one verified
    // site. Coincident coordinates must never merge separate locations.
    const key = listing.locationGroup
      ? `location:${listing.id}`
      : `coordinates:${listing.geo.lat.toFixed(5)},${listing.geo.lng.toFixed(5)}`;
    if (!groups.has(key)) {
      groups.set(key, { lat: listing.geo.lat, lng: listing.geo.lng, items: [] });
    }
    groups.get(key).items.push(listing);
  });
  return [...groups.values()];
};

const isAvailable = listing =>
  listing.locationGroup
    ? listing.availableCount > 0
    : getListingAvailability(listing.availabilityData).available;

const getComparablePrice = items => {
  const eligible = items.filter(isAvailable);
  // An unpriced available office can be cheaper than every known price. Do not
  // promote a minimum until all compared offers have usable prices.
  if (
    !eligible.length ||
    eligible.some(
      item =>
        !Number.isFinite(item.price?.amount) ||
        item.price.amount < 0 ||
        !/^[A-Z]{3}$/.test(item.price.currency || '')
    )
  )
    return null;
  const reference = eligible[0];
  if (
    eligible.some(
      item =>
        item.price.currency !== reference.price.currency ||
        item.unit !== reference.unit ||
        item.offerType !== reference.offerType
    )
  )
    return null;
  return eligible.reduce((a, b) => (a.price.amount <= b.price.amount ? a : b));
};

const createMarkerEl = (label, count) => {
  const wrapper = document.createElement('div');
  wrapper.className = 'nam-marker';
  const inner = document.createElement('div');
  inner.className = 'nam-marker-inner';
  inner.textContent = label;
  if (count && count > 1) {
    const badge = document.createElement('span');
    badge.className = 'nam-marker-count';
    badge.textContent = String(count);
    inner.appendChild(badge);
  }
  wrapper.appendChild(inner);
  return wrapper;
};

/**
 * Sticky search map for the Nos Annonces page.
 *
 * Props:
 *   - listings: array of plain objects with { id, title, geo: {lat, lng},
 *               formattedPrice, streetCity, slug }
 *   - activeId: string | null — currently hovered listing id
 *   - onMarkerHover: (id|null) => void — called on marker enter/leave
 *   - onLocationClick: (id) => void — expand this location in the results
 *   - locationGroup entries carry listingCount, availableCount and a comparable
 *     minimum price computed from their available matching offices.
 *
 * The map auto-fits on the very first render after listings load, but
 * does not re-fit when filters or the active id change, so the user
 * keeps their pan/zoom while interacting with cards.
 */
const NosAnnoncesMap = ({
  listings = [],
  activeId = null,
  onMarkerHover,
  onLocationClick,
  visible = true,
}) => {
  const callbacksRef = useRef({ onMarkerHover, onLocationClick });
  callbacksRef.current = { onMarkerHover, onLocationClick };
  const [availabilityDay, setAvailabilityDay] = useState(() => getParisDateKey());
  useEffect(() => {
    const refresh = () => setAvailabilityDay(getParisDateKey());
    const timer = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, []);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map()); // listing id -> { el, marker, popup }
  const fittedOnceRef = useRef(false);
  const [mapboxReady, setMapboxReady] = useState(
    typeof window !== 'undefined' && !!window.mapboxgl
  );

  useEffect(() => {
    ensureStyles();
  }, []);

  useEffect(() => {
    if (mapboxReady || typeof window === 'undefined') return undefined;
    const id = setInterval(() => {
      if (window.mapboxgl) {
        setMapboxReady(true);
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
  }, [mapboxReady]);

  // Init map once
  useEffect(() => {
    if (!mapboxReady || !containerRef.current || mapRef.current) return undefined;
    const map = new window.mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [2.3488, 48.8534],
      zoom: 10.5,
    });
    map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;
    // Mobile switches and responsive columns can resize without a window resize.
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (containerRef.current?.clientWidth) map.resize();
          })
        : null;
    observer?.observe(containerRef.current);
    return () => {
      observer?.disconnect();
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current.clear();
      map.remove();
      mapRef.current = null;
      fittedOnceRef.current = false;
    };
  }, [mapboxReady]);

  // Stable key to know when listings really changed
  const listingsKey = useMemo(
    () =>
      listings
        .filter(l => l?.geo && Number.isFinite(l.geo.lat) && Number.isFinite(l.geo.lng))
        .map(l => JSON.stringify(l))
        .join(','),
    [listings]
  );

  // Render markers on listings change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.mapboxgl) return undefined;

    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      // A map first mounted in the hidden mobile panel still has its fallback
      // dimensions. Measure its visible panel before calculating the bounds.
      if (containerRef.current?.clientWidth) map.resize();

      // Clear previous markers
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current.clear();

      if (!listings.length) return;

      const groups = groupByCoords(listings);
      const bounds = new window.mapboxgl.LngLatBounds();
      let added = 0;

      groups.forEach(group => {
        const items = group.items;
        const location = items.length === 1 && items[0].locationGroup ? items[0] : null;
        const cheapest = getComparablePrice(items);
        const minPrice = cheapest?.price;
        const count = location ? location.listingCount : items.length;
        const label = minPrice
          ? `${
              location || items.length > 1 || cheapest.hasCommitmentPrices ? 'Dès ' : ''
            }${formatPriceLabel(minPrice)}`
          : !items.some(isAvailable)
          ? 'Indisponible'
          : count > 1
          ? `${count} ${location ? 'bureaux' : 'annonces'}`
          : 'Prix à confirmer';
        const el = createMarkerEl(label, count);

        // Order items cheapest first for the grouped list
        const sorted = [...items].sort(
          (a, b) => (a.price?.amount ?? Infinity) - (b.price?.amount ?? Infinity)
        );

        let popupHtml;
        if (location) {
          popupHtml = buildLocationPopupHtml(location);
        } else if (sorted.length === 1) {
          const target = sorted[0];
          popupHtml = buildSinglePopupHtml({
            title: target.title,
            availabilityLabel: getListingAvailability(target.availabilityData).available
              ? null
              : getListingAvailability(target.availabilityData).label,
            streetCity: target.streetCity,
            formattedPrice: target.price
              ? `${target.hasCommitmentPrices ? 'Dès ' : ''}${formatPriceLabel(target.price) || ''}`
              : null,
            unit: target.unit,
            offerType: target.offerType,
            slug: target.slug,
            id: target.id,
            imageUrl: target.imageUrl,
            sqm: target.sqm,
            cap: target.cap,
            authorName: target.authorName,
          });
        } else {
          const listItems = sorted.map(l => ({
            id: l.id,
            slug: l.slug,
            bureauLabel: buildBureauLabel(l),
            availabilityLabel: getListingAvailability(l.availabilityData).available
              ? null
              : getListingAvailability(l.availabilityData).label,
            sqm: l.sqm,
            cap: l.cap,
            formattedPrice: l.price
              ? `${l.hasCommitmentPrices ? 'Dès ' : ''}${formatPriceLabel(l.price)} · ${l.unit}`
              : '',
          }));
          const cover = sorted.find(l => l.imageUrl)?.imageUrl || null;
          popupHtml = buildGroupPopupHtml({
            // This is only a map coordinate cluster, not a business location.
            groupTitle: 'Annonces à cette adresse',
            streetCity: sorted[0].streetCity,
            items: listItems,
            imageUrl: cover,
            authorName: null,
            minFormattedPrice: minPrice ? `${formatPriceLabel(minPrice)} · ${cheapest.unit}` : '',
          });
        }

        const popup = new window.mapboxgl.Popup({
          offset: 18,
          closeButton: false,
          closeOnClick: false,
          // Hover previews must not steal focus and scroll the page to the map.
          focusAfterOpen: false,
          className: 'nam-popup',
        }).setHTML(popupHtml);

        if (location) {
          const onLocationButtonClick = event => {
            const button = event.target.closest('[data-location-id]');
            if (!button) return;
            event.preventDefault();
            callbacksRef.current.onLocationClick?.(location.id);
          };
          popup.on('open', () => {
            const popupElement = popup.getElement();
            popupElement?.removeEventListener('click', onLocationButtonClick);
            popupElement?.addEventListener('click', onLocationButtonClick);
          });
        }

        // Use the first (cheapest) listing in the group as the canonical id
        // for hover sync with the card column.
        const primaryId = sorted[0]?.id;
        const handleEnter = () => {
          if (primaryId) callbacksRef.current.onMarkerHover?.(primaryId);
        };
        const handleLeave = () => {
          callbacksRef.current.onMarkerHover?.(null);
        };

        el.addEventListener('mouseenter', handleEnter);
        el.addEventListener('mouseleave', handleLeave);

        const marker = new window.mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([group.lng, group.lat])
          .setPopup(popup)
          .addTo(map);

        items.forEach(item => {
          markersRef.current.set(item.id, { el, marker, popup, group });
        });

        bounds.extend([group.lng, group.lat]);
        added += 1;
      });

      const canFit = containerRef.current?.clientWidth > 0;
      if (canFit && !fittedOnceRef.current && added >= 2) {
        try {
          map.fitBounds(bounds, { padding: 60, maxZoom: 13, duration: 0 });
        } catch (e) {
          // ignore
        }
        fittedOnceRef.current = true;
      } else if (canFit && !fittedOnceRef.current && added === 1) {
        map.jumpTo({ center: bounds.getCenter(), zoom: 13 });
        fittedOnceRef.current = true;
      }
    };

    if (map.loaded() && map.isStyleLoaded()) {
      apply();
    } else {
      map.once('load', apply);
    }

    return () => {
      cancelled = true;
    };
  }, [listingsKey, mapboxReady, visible, availabilityDay]); // eslint-disable-line

  // React to external active id changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach(({ el, popup }) => {
      el.classList.remove('is-active');
      if (popup.isOpen()) popup.remove();
    });

    if (activeId) {
      const entry = markersRef.current.get(activeId);
      if (entry) {
        entry.el.classList.add('is-active');
        try {
          entry.marker.togglePopup();
        } catch (e) {
          // ignore
        }

        // Keep the popup open while the user hovers the popup itself —
        // this lets them scroll the grouped list, click 'Voir l'annonce',
        // etc. without the popup vanishing the second they leave the
        // marker/card. We piggy-back on onMarkerHover so the parent's
        // debounced clear is the one being cancelled / scheduled.
        const popupEl = entry.popup.getElement();
        if (popupEl && typeof onMarkerHover === 'function') {
          const onPopupEnter = () => onMarkerHover(activeId);
          const onPopupLeave = () => onMarkerHover(null);
          popupEl.addEventListener('mouseenter', onPopupEnter);
          popupEl.addEventListener('mouseleave', onPopupLeave);
          // Cleanup on next activeId change
          entry._popupHandlers = { popupEl, onPopupEnter, onPopupLeave };
        }
      }
    }

    return () => {
      markersRef.current.forEach(entry => {
        if (entry._popupHandlers) {
          const { popupEl, onPopupEnter, onPopupLeave } = entry._popupHandlers;
          popupEl.removeEventListener('mouseenter', onPopupEnter);
          popupEl.removeEventListener('mouseleave', onPopupLeave);
          entry._popupHandlers = null;
        }
      });
    };
  }, [activeId, onMarkerHover, listingsKey, mapboxReady, visible, availabilityDay]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: 400,
        borderRadius: 'inherit',
        overflow: 'hidden',
      }}
    />
  );
};

export default NosAnnoncesMap;
